import { useQuery } from '@tanstack/react-query'
import { LoginMethod, type LoginOption } from '@today/types'
import { useEffect, useRef } from 'react'
import { authService } from '@/services/auth.service'
import { tenantAuthenticationService } from '@/services/tenant-authentication.service'

/** Native sign-in — the server's answer for an unknown tenant, and the fallback when the lookup fails. */
const NATIVE: LoginOption = { method: LoginMethod.NATIVE, identityProvider: 'COGNITO', providerId: null, name: null }

const signIn = (option: LoginOption) => {
	void authService.initiateLogin({ identityProvider: option.identityProvider })
}

/**
 * The per-tenant login surface's logic (TO-410 REQ-008): resolve the tenant's
 * sign-in options. Exactly one option redirects straight to the hosted UI with
 * that `identity_provider`; several are offered as a choice. A failed lookup
 * offers native sign-in rather than stranding the user; it is not followed
 * automatically, so a broken lookup never loops through the hosted UI. It also
 * answers the tenant's brand id, which the page binds (TO-541 REQ-104).
 */
export function useTenantLoginOptions(slug: string) {
	const query = useQuery({
		queryKey: ['tenant-login-options', slug],
		queryFn: () => tenantAuthenticationService.getLoginOptions(slug),
	})
	const listed = query.data?.options
	const only = listed?.length === 1 ? listed[0] : null
	const redirected = useRef(false)

	useEffect(() => {
		if (!only || redirected.current) return
		redirected.current = true
		signIn(only)
	}, [only])

	const options = query.isError || listed?.length === 0 ? [NATIVE] : (listed ?? null)
	// TO-541 REQ-104: the tenant's brand id; null while pending, after a failure, or when it has none.
	const brandId = query.data?.brandId ?? null
	return { options, brandId, isLoading: query.isLoading || only !== null, select: signIn }
}
